/**
 * Plays a `typewriter` tick on every printable keystroke into a text-y
 * input or textarea. One delegated listener at document level so the
 * sound is on every form on the site without per-component wiring.
 *
 * Pitch climbs slightly with the value's length so a long answer feels
 * like building momentum, capped so it never gets screechy. Throttled at
 * 25ms so held keys don't sound like a machine gun.
 *
 * No-op until the user opts in via MSoundToggle.
 */

const TEXT_INPUT_TYPES = new Set([
  'text',
  'email',
  'search',
  'tel',
  'url',
  'password',
  'number',
])

function isTypeable(el: EventTarget | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (!el || !(el instanceof HTMLElement))
    return false
  // Containers can opt out (e.g. BrandPlayfulForm fires its own multi-voice
  // typewriter per field and shouldn't double up).
  if (el.closest('[data-no-typewriter]'))
    return false
  if (el.tagName === 'TEXTAREA')
    return true
  if (el.tagName !== 'INPUT')
    return false
  const input = el as HTMLInputElement
  // Skip honeypots and hidden fields.
  if (input.type === 'hidden' || input.tabIndex < 0)
    return false
  return TEXT_INPUT_TYPES.has(input.type) || input.type === ''
}

export default defineNuxtPlugin(() => {
  const { play } = useSound()

  document.addEventListener('input', (e) => {
    const target = e.target
    if (!isTypeable(target))
      return
    const ie = e as InputEvent
    const inputType = ie.inputType ?? ''
    const len = target.value.length

    if (inputType.startsWith('delete')) {
      // Backspace / forward-delete: a soft swoosh whose pitch drops as
      // the field shortens, mirroring the typewriter's rising climb in
      // reverse.
      const detune = -Math.min((100 - len) * 8, 400)
      play('delete', {
        gain: 0.65,
        detune,
        throttle: 30,
        rateJitter: 0.1,
      })
      return
    }

    // Skip programmatic / paste events so only real typing fires the tick.
    if (inputType && !inputType.startsWith('insert'))
      return

    const climb = Math.min(len * 18, 600)
    play('typewriter', {
      gain: 0.7,
      detune: climb,
      throttle: 25,
      rateJitter: 0.1,
    })
  })
})
